/**
 * credentialsEngine.js — Rê Leve
 *
 * Painel de gestão de credenciais e uso de IA.
 *
 * IMPORTANTE: chaves secretas NÃO ficam no frontend. O painel apenas
 * registra metadados (status, mascarada, data de configuração) e exibe
 * uso de tokens vindo do backend (/api/usage). A chave real vai no
 * Render → Backend → Environment.
 *
 * Para o cliente ver "como configurar", o painel mostra um passo-a-passo.
 */

const LS_KEY = 'releve.credentials.meta';

const SEED_META = {
  openai: {
    name: 'OpenAI (LLM)',
    envVar: 'OPENAI_API_KEY',
    status: 'pending',  // pending | configured | active | error
    maskedKey: '',
    configuredAt: null,
    notes: 'Necessária para Léve responder com fluência. Modelo: gpt-4.1-mini (custo baixo).',
    setupSteps: [
      'Criar conta em platform.openai.com',
      'Ir em "API keys" → "Create new secret key"',
      'Copiar a chave (começa com sk-...)',
      'No Render, abrir o backend "releve-api"',
      'Environment → adicionar OPENAI_API_KEY com o valor da chave',
      'Salvar — Render rebuilds automaticamente em ~2 min',
      'Voltar aqui, clicar "Atualizar status"',
    ],
    costInfo: 'gpt-4.1-mini: ~US$ 0,15 por milhão de tokens input · US$ 0,60 por milhão de output. Estimativa: ~R$ 0,01 por conversa típica.',
  },
  n8n: {
    name: 'n8n (Automações)',
    envVar: 'N8N_WEBHOOK_URL',
    status: 'pending',
    maskedKey: '',
    configuredAt: null,
    notes: 'URL pública do webhook do n8n. Recebe eventos de pagamento, handoff humano e guardrail acionado.',
    setupSteps: [
      'Criar instância n8n (n8n.cloud ou self-hosted)',
      'Criar workflow novo com trigger "Webhook"',
      'Copiar a URL gerada (https://...)',
      'No Render → backend → Environment → N8N_WEBHOOK_URL = essa URL',
      'Salvar e testar via "Testar conexão" abaixo',
    ],
    costInfo: 'n8n self-hosted: gratuito (custo só de servidor). n8n.cloud: a partir de US$ 20/mês.',
  },
  stripe: {
    name: 'Stripe (Pagamentos)',
    envVar: 'STRIPE_SECRET_KEY',
    status: 'pending',
    maskedKey: '',
    configuredAt: null,
    notes: 'Pagamento Pix + cartão direto pelo app. Cartão jamais passa pelo nosso backend (PCI-DSS).',
    setupSteps: [
      'Criar conta em stripe.com (Brasil)',
      'Ativar conta com CNPJ + dados bancários',
      'Pegar a chave secreta em Developers → API Keys',
      'No Render → backend → Environment → STRIPE_SECRET_KEY',
      'Configurar webhook em Stripe → Webhooks → criar endpoint',
      'Adicionar STRIPE_WEBHOOK_SECRET no Render também',
    ],
    costInfo: 'Stripe Brasil: 3.99% + R$ 0,39 por transação no cartão · Pix: 0.99% sem mensalidade.',
  },
  neon: {
    name: 'Neon PostgreSQL',
    envVar: 'DATABASE_URL',
    status: 'pending',
    maskedKey: '',
    configuredAt: null,
    notes: 'Banco de dados principal. Persiste clientes, pedidos, conversas, RAG. Free tier: 3 GB.',
    setupSteps: [
      'Criar conta em neon.tech (login com GitHub funciona)',
      'Criar projeto novo (região: us-east — mais barato)',
      'Copiar a connection string (formato postgres://user:pass@host/db)',
      'No Render → backend → Environment → DATABASE_URL',
      'Rodar migration: npm run migrate (no terminal do backend Render)',
    ],
    costInfo: 'Free tier: 3 GB dados + 100 horas/mês compute. Pro: US$ 19/mês.',
  },
  whatsapp: {
    name: 'WhatsApp Business',
    envVar: 'VITE_WHATSAPP_NUMBER',
    status: 'pending',
    maskedKey: '',
    configuredAt: null,
    notes: 'Número no formato internacional: 5581987654321 (55=BR, 81=PE, número). Coloca direto no Render → frontend.',
    setupSteps: [
      'Ter o WhatsApp Business da Rê Leve já criado',
      'Pegar o número completo (com DDD)',
      'No Render → frontend → Environment → VITE_WHATSAPP_NUMBER = 5581...',
      'Setar VITE_ENABLE_REAL_WHATSAPP = true',
      'Render rebuild automático — todos os botões wa.me ficam reais',
    ],
    costInfo: 'WhatsApp Business: gratuito até 1.000 conversas/mês iniciadas pela empresa. Após isso, pagamento por categoria de mensagem.',
  },
};

function loadMeta() {
  if (typeof window === 'undefined') return SEED_META;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) { localStorage.setItem(LS_KEY, JSON.stringify(SEED_META)); return SEED_META; }
    return { ...SEED_META, ...JSON.parse(raw) };
  } catch { return SEED_META; }
}

function saveMeta(meta) {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem(LS_KEY, JSON.stringify(meta)); } catch {}
}

// ── API pública ────────────────────────────────────────────────────────────
export function getCredentials() { return loadMeta(); }

export function setCredentialStatus(id, status) {
  const meta = loadMeta();
  if (!meta[id]) return;
  meta[id].status = status;
  meta[id].configuredAt = status === 'configured' || status === 'active'
    ? new Date().toISOString()
    : meta[id].configuredAt;
  saveMeta(meta);
  return meta[id];
}

// ── Uso de tokens OpenAI ─────────────────────────────────────────────────
// Em produção, o backend expõe /api/usage retornando dados reais.
// Aqui simulamos para o painel renderizar — quando a chave for ativa,
// substituir por fetch('/api/usage').

export async function getOpenAIUsage() {
  // Tentativa de buscar do backend real
  if (typeof window !== 'undefined' && window.fetch) {
    try {
      const apiBase = import.meta.env.VITE_API_BASE_URL || '';
      if (apiBase) {
        const r = await fetch(apiBase.replace(/\/$/, '') + '/api/usage', {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
        });
        if (r.ok) {
          const data = await r.json();
          return { source: 'backend', ...data };
        }
      }
    } catch { /* fallback abaixo */ }
  }

  // Fallback: estimativa local (sem dados reais) — exibido com label "estimativa"
  return {
    source: 'estimate',
    period: 'mês atual (estimativa)',
    requests:       0,
    inputTokens:    0,
    outputTokens:   0,
    totalTokens:    0,
    estimatedCostUSD: 0,
    estimatedCostBRL: 0,
    perDay: [],
    notes: 'Sem dados do backend ainda. Quando OPENAI_API_KEY estiver configurada no Render, este painel mostra uso real.',
  };
}

// Helpers de formatação
export function formatTokens(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'k';
  return String(n);
}
export function formatUSD(v) {
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
export function formatBRL(v) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
